import * as React from 'react'
import { motion, animate, useMotionValue, useTransform } from 'motion/react'

import { Badge, EmptyState } from '@buffer-mono/popcorn'

import styles from './CommentScoreIncreased.module.css'

const EmptyStateIconMotion = motion.create(EmptyState.Icon)

export const CommentScoreIncreased = ({
  score,
  scoreDelta,
}: {
  score: number
  scoreDelta: number
}): JSX.Element => {
  const progress = useMotionValue(0)
  const roundedProgress = useTransform(() => Math.round(progress.get()))
  const filter = useTransform(progress, [0, score], ['blur(10px)', 'blur(0px)'])
  const scale = useTransform(progress, [0, score], [0.5, 1])
  const opacity = useTransform(progress, [0, score], [0.5, 1])

  const maxDuration = 2 // seconds for 100
  const minDuration = 0.5 // minimum duration for very low values
  const duration = Math.max((score / 100) * maxDuration, minDuration)

  React.useEffect(() => {
    animate(progress, score, {
      duration,
      ease: [0.31, 0.05, 0.28, 0.85],
    })
  }, [progress, duration, score])

  return (
    <EmptyState
      size="large"
      variant="success"
      role="status"
      aria-label="All comments have been addressed"
      className={styles.wrapper}
    >
      <div className={styles.animationContainer} aria-hidden="true">
        <EmptyStateIconMotion
          className={styles.emptyStateIcon}
          animate={{
            scale: [1, 1.2, 1],
            transition: {
              duration: 1,
            },
            backgroundColor: [
              'var(--color-bg-success-subtle)',
              'var(--global-color-green-300)',
              'var(--color-bg-success-subtle)',
            ],
          }}
        />
        <motion.div
          className={styles.commentScoreIncreasedText}
          style={{
            filter,
            scale,
            opacity,
            willChange: 'filter, transform, opacity',
          }}
        >
          {roundedProgress}
        </motion.div>
        <Badge
          data-testid="score-delta"
          size="medium"
          className={styles.scoreDelta}
        >
          +{scoreDelta}
        </Badge>
      </div>
      <EmptyState.Heading>
        Nice job increasing your Comment Score! 🤩 Keep it up by consistently
        replying to new comments.
      </EmptyState.Heading>
      <EmptyState.Description>
        You&apos;re all caught up! We will let you know when new comments
        appear.
      </EmptyState.Description>
    </EmptyState>
  )
}
